/**
 * Client management (T4.7) and per-blueprint administration (T4.7a).
 *
 *   /admin/clients             every client; create one
 *   /admin/clients/:clientId   one client: its terminology, and its blueprints
 *   /admin/blueprints/:id      one blueprint: its terminology, and deletion
 *
 * Words. The list and client screens span blueprints of several clients, so they
 * speak in the system defaults (design.md, "Cross-client surfaces"). The
 * blueprint screen is about ONE blueprint, so its prose uses that blueprint's
 * resolved terms. The editor's own column of standard names is data from
 * DEFAULT_TERMINOLOGY, not text in this file.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DEFAULT_TERMINOLOGY, resolveTerms } from '@blueprint/shared';
import { TermsProvider, useTerms } from '../../lib/terms';
import { useDocumentTitle } from '../../lib/documentTitle';
import { useBlueprint, type BlueprintSummary } from '../../lib/blueprints';
import {
  errorText,
  useAllBlueprints,
  useClients,
  useCreateBlueprint,
  useCreateClient,
  useRenameBlueprint,
  useRenameClient,
  useSetBlueprintTerminology,
  useSetClientTerminology,
  type AdminClient,
} from '../../lib/admin';
import { Dialog } from '../versions/Dialog';
import { RenameDialog } from './RenameDialog';
import { SoftDeleteDialog, type DeletionTarget } from './BlueprintDeletion';
import { TerminologyEditor, type InheritSource } from './TerminologyEditor';
import { FormError, SLUG_PATTERN, slugify } from './ui';

/* ------------------------------------------------------------ client list */

export function ClientList() {
  const terms = useTerms();
  const clients = useClients();
  const blueprints = useAllBlueprints();
  const [creating, setCreating] = useState(false);

  useDocumentTitle('Clients', terms.plural('blueprint'));

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of blueprints.data ?? []) m.set(b.client.id, (m.get(b.client.id) ?? 0) + 1);
    return m;
  }, [blueprints.data]);

  const rows = [...(clients.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));

  return (
    <div className="sheet adm-sheet">
      <div className="masthead">
        <div>
          <h1>Clients</h1>
          <p className="meta">
            Each client owns its {terms.plural('blueprint')} and can have its own words for them.
          </p>
        </div>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          Add client
        </button>
      </div>

      {clients.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      ) : clients.isError ? (
        <div className="empty" role="alert">
          Could not load the clients.
          <br />
          {errorText(clients.error)}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty">There are no clients yet.</div>
      ) : (
        <div className="adm-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Slug</th>
                <th scope="col">{terms.plural('blueprint')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <th scope="row" className="adm-name">
                    <Link to={`/admin/clients/${c.id}`}>{c.name}</Link>
                  </th>
                  <td>
                    <code className="adm-code">{c.slug}</code>
                  </td>
                  <td>{blueprints.isPending ? '…' : (counts.get(c.id) ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} title="Add client" onClose={() => setCreating(false)}>
        {creating ? <CreateClientForm onClose={() => setCreating(false)} /> : null}
      </Dialog>
    </div>
  );
}

function CreateClientForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = SLUG_PATTERN.test(effectiveSlug) && effectiveSlug.length <= 64;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (create.isPending || !name.trim() || !slugOk) return;
    create.mutate(
      { name: name.trim(), slug: effectiveSlug },
      { onSuccess: (c) => navigate(`/admin/clients/${c.id}`) },
    );
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <label className="field">
        <span className="field-label">Client name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
      </label>
      <label className="field">
        <span className="field-label">Slug</span>
        <input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={effectiveSlug && !slugOk ? true : undefined}
        />
        <span className="adm-help">
          Lowercase letters, digits and single hyphens. Must be unique across clients.
        </span>
        {effectiveSlug && !slugOk ? (
          <span className="adm-field-err" role="alert">
            Use lowercase letters, digits and single hyphens only, up to 64 characters.
          </span>
        ) : null}
      </label>
      {create.isError ? <FormError>{errorText(create.error)}</FormError> : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn primary"
          disabled={create.isPending || !name.trim() || !slugOk}
        >
          {create.isPending ? 'Adding…' : 'Add client'}
        </button>
      </div>
    </form>
  );
}

/* ----------------------------------------------------------- client detail */

function BackLink({ to, children }: { to: string; children: string }) {
  return (
    <p className="adm-back">
      <Link to={to}>← {children}</Link>
    </p>
  );
}

export function ClientDetail() {
  const { clientId } = useParams();
  const clients = useClients();
  const blueprints = useAllBlueprints();
  const terms = useTerms();

  const client = clients.data?.find((c) => c.id === clientId);
  useDocumentTitle(client?.name ?? 'Client', terms.plural('blueprint'));

  if (clients.isPending || blueprints.isPending) {
    return (
      <div className="sheet adm-sheet">
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      </div>
    );
  }
  if (clients.isError || blueprints.isError) {
    return (
      <div className="sheet adm-sheet">
        <div className="empty" role="alert">
          Could not load this client.
          <br />
          {errorText(clients.error ?? blueprints.error)}
        </div>
      </div>
    );
  }
  if (!client) {
    return (
      <div className="sheet adm-sheet">
        <BackLink to="/admin/clients">All clients</BackLink>
        <div className="empty" role="alert">
          There is no client with that address.
        </div>
      </div>
    );
  }
  return (
    <ClientBody client={client} mine={blueprints.data.filter((b) => b.client.id === client.id)} />
  );
}

function ClientBody({ client, mine }: { client: AdminClient; mine: readonly BlueprintSummary[] }) {
  const terms = useTerms();
  const setTerms = useSetClientTerminology();
  const rename = useRenameClient();
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DeletionTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  return (
    <div className="sheet adm-sheet">
      <BackLink to="/admin/clients">All clients</BackLink>
      <div className="masthead">
        <div>
          <h1>{client.name}</h1>
          <p className="meta">
            Slug <code className="adm-code">{client.slug}</code>
          </p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => {
            rename.reset();
            setRenaming(true);
          }}
        >
          Rename client
        </button>
      </div>

      {notice ? (
        <p className="status adm-notice" role="status">
          {notice}
        </p>
      ) : null}

      <section className="adm-section" aria-labelledby="cl-bps">
        <div className="adm-section-head">
          <h2 id="cl-bps">{terms.plural('blueprint')}</h2>
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            Add {terms.noun('blueprint')}
          </button>
        </div>
        {mine.length === 0 ? (
          <div className="empty">This client has no {terms.plural('blueprint')} yet.</div>
        ) : (
          <ul className="adm-list">
            {[...mine]
              .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'))
              .map((b) => (
                <li key={b.id} className="adm-list-row">
                  <div className="adm-list-main">
                    <Link to={`/blueprints/${b.id}`} className="adm-name-link">
                      {b.name}
                    </Link>
                    {b.isArchived ? <span className="adm-tag">Archived</span> : null}
                    <code className="adm-code">{b.slug}</code>
                  </div>
                  <div className="adm-actions">
                    <Link className="btn" to={`/admin/blueprints/${b.id}`}>
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="btn"
                      onClick={() =>
                        setDeleting({ id: b.id, name: b.name, slug: b.slug, clientName: b.client.name })
                      }
                    >
                      Delete ‘{b.name}’
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </section>

      <section className="adm-section" aria-labelledby="cl-terms">
        <h2 id="cl-terms">Terminology</h2>
        <p className="adm-lede">
          These words apply to every {terms.noun('blueprint')} of {client.name}, unless one of them
          sets its own. Nothing here is versioned: a change shows immediately, on published
          versions too.
        </p>
        <TerminologyEditor
          key={JSON.stringify(client.terminology)}
          inherited={DEFAULT_TERMINOLOGY}
          sourceOf={() => 'system default'}
          initial={client.terminology}
          levelName={`every ${terms.noun('blueprint')} of ${client.name}`}
          saving={setTerms.isPending}
          saveError={setTerms.isError ? errorText(setTerms.error) : null}
          savedNotice={saved}
          onSave={(terminology) => {
            setSaved(null);
            setTerms.mutate(
              { id: client.id, terminology },
              { onSuccess: () => setSaved(`Saved. ${client.name} now uses these words.`) },
            );
          }}
        />
      </section>

      <Dialog open={creating} title={`Add ${terms.noun('blueprint')}`} onClose={() => setCreating(false)}>
        {creating ? (
          <CreateBlueprintForm
            client={client}
            onClose={() => setCreating(false)}
            onDone={(name) => {
              setCreating(false);
              setNotice(`‘${name}’ was added to ${client.name}.`);
            }}
          />
        ) : null}
      </Dialog>

      <RenameDialog
        open={renaming}
        title="Rename client"
        fieldLabel="Client name"
        currentName={client.name}
        slug={client.slug}
        pending={rename.isPending}
        error={rename.isError ? errorText(rename.error) : null}
        onClose={() => setRenaming(false)}
        onSubmit={(name) =>
          rename.mutate(
            { id: client.id, name },
            {
              onSuccess: () => {
                setRenaming(false);
                setNotice(`Renamed to ‘${name}’. The slug is still ${client.slug}.`);
              },
            },
          )
        }
      />

      <SoftDeleteDialog
        target={deleting}
        onClose={() => setDeleting(null)}
        onDone={(t) => {
          setDeleting(null);
          setNotice(`‘${t.name}’ was moved to the archive. It can be restored from there.`);
        }}
      />
    </div>
  );
}

function CreateBlueprintForm({
  client,
  onClose,
  onDone,
}: {
  client: AdminClient;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const create = useCreateBlueprint();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = SLUG_PATTERN.test(effectiveSlug) && effectiveSlug.length <= 64;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (create.isPending || !name.trim() || !slugOk) return;
    create.mutate(
      {
        clientId: client.id,
        name: name.trim(),
        slug: effectiveSlug,
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      { onSuccess: (b) => onDone(b.name) },
    );
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <p>For {client.name}.</p>
      <label className="field">
        <span className="field-label">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
      </label>
      <label className="field">
        <span className="field-label">Slug</span>
        <input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={effectiveSlug && !slugOk ? true : undefined}
        />
        <span className="adm-help">
          Lowercase letters, digits and single hyphens. Unique within this client.
        </span>
        {effectiveSlug && !slugOk ? (
          <span className="adm-field-err" role="alert">
            Use lowercase letters, digits and single hyphens only, up to 64 characters.
          </span>
        ) : null}
      </label>
      <label className="field">
        <span className="field-label">Description (optional)</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} autoComplete="off" />
      </label>
      {/* The API's own sentence, verbatim. For a slug held by a deleted blueprint it
          explains where the slug went and what to do, which "slug taken" would not. */}
      {create.isError ? <FormError>{errorText(create.error)}</FormError> : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn primary"
          disabled={create.isPending || !name.trim() || !slugOk}
        >
          {create.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------- blueprint detail */

export function BlueprintAdmin() {
  const { id } = useParams();
  const detail = useBlueprint(id);

  if (detail.isPending) {
    return (
      <div className="sheet adm-sheet">
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="sheet adm-sheet">
        <BackLink to="/admin/clients">All clients</BackLink>
        <div className="empty" role="alert">
          Could not load this item.
          <br />
          {errorText(detail.error)}
        </div>
      </div>
    );
  }
  // Prose on this screen uses this blueprint's own resolved words.
  return (
    <TermsProvider terminology={detail.data.terms}>
      <BlueprintAdminBody detail={detail.data} />
    </TermsProvider>
  );
}

function BlueprintAdminBody({
  detail,
}: {
  detail: NonNullable<ReturnType<typeof useBlueprint>['data']>;
}) {
  const terms = useTerms();
  const navigate = useNavigate();
  const { blueprint } = detail;
  const setTerms = useSetBlueprintTerminology();
  const rename = useRenameBlueprint();
  const [renaming, setRenaming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DeletionTarget | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useDocumentTitle(`${blueprint.name} settings`, terms.plural('blueprint'));

  const clientOverride = blueprint.client.terminology;
  const inherited = useMemo(() => resolveTerms(clientOverride, null), [clientOverride]);
  const initial = blueprint.terminology;

  const sourceOf = (
    f: { noun: 'blueprint' | 'room' | 'pillar' | 'sliver' | 'deliverable'; part: 'singular' | 'plural' } | {
      state: 'draft' | 'manuscript' | 'proof' | 'published';
    },
  ): InheritSource => {
    const set =
      'noun' in f
        ? clientOverride?.nouns?.[f.noun]?.[f.part]
        : clientOverride?.states?.[f.state]?.label;
    return set ? 'client' : 'system default';
  };

  return (
    <div className="sheet adm-sheet">
      <BackLink to={`/admin/clients/${blueprint.client.id}`}>{blueprint.client.name}</BackLink>
      <div className="masthead">
        <div>
          <h1>{blueprint.name}</h1>
          <p className="meta">
            {blueprint.client.name} · slug <code className="adm-code">{blueprint.slug}</code>
            {blueprint.isArchived ? ' · archived' : ''}
          </p>
        </div>
        <div className="adm-head-actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              rename.reset();
              setRenaming(true);
            }}
          >
            Rename {terms.noun('blueprint')}
          </button>
          <Link className="btn" to={`/blueprints/${blueprint.id}`}>
            Open {terms.noun('blueprint')}
          </Link>
        </div>
      </div>

      {notice ? (
        <p className="status adm-notice" role="status">
          {notice}
        </p>
      ) : null}

      <section className="adm-section" aria-labelledby="bp-terms">
        <h2 id="bp-terms">Terminology</h2>
        <p className="adm-lede">
          Overrides for this {terms.noun('blueprint')} only. Anything left empty uses the client’s
          word if {blueprint.client.name} has set one, and the system default otherwise. Nothing
          here is versioned.
        </p>
        <TerminologyEditor
          key={JSON.stringify(initial)}
          inherited={inherited}
          sourceOf={sourceOf}
          initial={initial}
          levelName={`this ${terms.noun('blueprint')}`}
          saving={setTerms.isPending}
          saveError={setTerms.isError ? errorText(setTerms.error) : null}
          savedNotice={saved}
          onSave={(terminology) => {
            setSaved(null);
            setTerms.mutate(
              { id: blueprint.id, terminology },
              { onSuccess: () => setSaved('Saved.') },
            );
          }}
        />
      </section>

      <section className="adm-section" aria-labelledby="bp-access">
        <h2 id="bp-access">Access</h2>
        <p className="adm-lede">
          Who can open this {terms.noun('blueprint')} is set on the{' '}
          <Link to="/admin/access">Access screen</Link>.
        </p>
      </section>

      <section className="adm-section adm-danger" aria-labelledby="bp-danger">
        <h2 id="bp-danger">Delete</h2>
        <p className="adm-lede">
          Deleting moves this {terms.noun('blueprint')} to the archive, where it can be restored.
          Permanent deletion is done from the archive.
        </p>
        <button
          type="button"
          className="btn danger"
          onClick={() =>
            setDeleting({
              id: blueprint.id,
              name: blueprint.name,
              slug: blueprint.slug,
              clientName: blueprint.client.name,
            })
          }
        >
          Delete ‘{blueprint.name}’
        </button>
      </section>

      <RenameDialog
        open={renaming}
        title={`Rename ${terms.noun('blueprint')}`}
        fieldLabel="Name"
        currentName={blueprint.name}
        slug={blueprint.slug}
        pending={rename.isPending}
        error={rename.isError ? errorText(rename.error) : null}
        onClose={() => setRenaming(false)}
        onSubmit={(name) =>
          rename.mutate(
            { id: blueprint.id, name },
            {
              onSuccess: () => {
                setRenaming(false);
                setNotice(`Renamed to ‘${name}’. The slug is still ${blueprint.slug}.`);
              },
            },
          )
        }
      />

      <SoftDeleteDialog
        target={deleting}
        onClose={() => setDeleting(null)}
        onDone={() => navigate(`/admin/clients/${blueprint.client.id}`)}
      />
    </div>
  );
}
